import { useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext.jsx";
import {
  getAndroidPlayStoreUrl,
  getIosAppStoreUrl,
} from "../lib/mobileAppStore.js";
import { StoreBrandIcon } from "./StoreBrandIcon.jsx";

function storeLinks() {
  const links = [];
  const iosUrl = getIosAppStoreUrl();
  const androidUrl = getAndroidPlayStoreUrl();

  if (iosUrl) {
    links.push({ id: "ios", label: "App Store", href: iosUrl });
  }

  links.push({ id: "android", label: "Google Play", href: androidUrl });
  return links;
}

function StoreLink({ link, className, onNavigate }) {
  return (
    <a
      href={link.href}
      className={className}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onNavigate}
    >
      <StoreBrandIcon store={link.id} />
      {link.label}
    </a>
  );
}

const dropdownItemClassName =
  "flex items-center gap-2.5 px-3 py-2 text-sm font-medium text-hc-text-muted transition hover:bg-white/5 hover:text-hc-text";

export default function OpenAppMenu({ layout = "dropdown", onNavigate }) {
  const { status } = useAuth();
  const detailsRef = useRef(null);
  const links = storeLinks();

  useEffect(() => {
    if (layout !== "dropdown") return undefined;

    function handlePointerDown(event) {
      const details = detailsRef.current;
      if (!details?.open) return;
      if (!details.contains(event.target)) {
        details.open = false;
      }
    }

    function handleKeyDown(event) {
      if (event.key === "Escape" && detailsRef.current?.open) {
        detailsRef.current.open = false;
      }
    }

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [layout]);

  if (status !== "authenticated" || links.length === 0) return null;

  if (layout === "menu") {
    return (
      <div className="flex flex-col gap-0.5">
        <p className="px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-hc-text-dim">
          Get the app
        </p>
        {links.map((link) => (
          <StoreLink
            key={link.id}
            link={link}
            className="flex items-center gap-2.5 rounded-[12px] px-3 py-2.5 text-sm font-medium text-hc-text-dim transition hover:bg-white/5 hover:text-hc-text"
            onNavigate={onNavigate}
          />
        ))}
      </div>
    );
  }

  return (
    <details ref={detailsRef} className="group relative">
      <summary className="inline-flex cursor-pointer list-none items-center gap-1.5 rounded-[10px] px-2 py-1.5 text-xs font-medium text-hc-text-muted transition hover:bg-white/5 [&::-webkit-details-marker]:hidden">
        Get the app
        <i
          className="icon-chevron-down text-[10px] leading-none opacity-80 transition group-open:rotate-180"
          aria-hidden="true"
        />
      </summary>

      <div className="absolute right-0 top-full z-50 mt-2 w-44 overflow-hidden rounded-[10px] border border-hc-surface-border-strong bg-hc-background py-1 shadow-[0_12px_28px_rgba(0,0,0,0.45)]">
        {links.map((link) => (
          <StoreLink key={link.id} link={link} className={dropdownItemClassName} />
        ))}
      </div>
    </details>
  );
}
